/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  getEmailTemplateSummaries,
  postRenderAuthorizationEmailTemplate,
  postSendRenderAuthorizationEmailTemplate
} from 'modules/emailTemplates/store/emailTemplate.actions';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { getAuthorizationComments, getAuthorizationSentTemplates } from 'modules/authorizations/store/actions/authorizations.actions';
import Hint from 'modules/common/hint.component';
import AdvancedSearchComponent from 'modules/common/advancedSearch.component';
import { POST_SEND_RENDER_AUTHORIZATION_EMAIL_TEMPLATE_FAILURE } from '../store/emailTemplateTypes';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { EmailEditorTiny } from '../emailEditors/EmailEditorTiny';
import { createFilteredRegardingOptions } from '../adminEmailTemplate/adminEmailTemplateView.container';

const STEP_SELECT = 1;
const STEP_EDIT = 2;
const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
// const DSP_ELIGIBLE_SERVICES = ['946f7f77-2a35-4d86-9b18-616f927f7124', '7d4d3d04-66b8-4d44-9131-5c7b2cf8b1f6'];

const isLikelyEmail = value => /[^\s@]+@[^\s@]+\.[^\s@]+/.test((value || '').trim());

const normalizeAndDeduplicateEmails = values => {
  const seen = new Set();
  const deduped = [];

  (values || []).forEach(value => {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      return;
    }

    const key = trimmed.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(trimmed);
    }
  });

  return deduped;
};

// Columns of the shared vendor search used to pick the recipient of a vendor letter
const VENDOR_SEARCH_COLUMNS = [
  { name: 'vendorId', title: 'Id' },
  { name: 'companyName', title: 'Company Name' },
  { name: 'city', title: 'City' },
  { name: 'officeEmail', title: 'Office Email' }
];

const VENDOR_SEARCH_COLUMN_EXTENSIONS = [
  { columnName: 'vendorId', width: 100 },
  { columnName: 'companyName', width: 300 },
  { columnName: 'city', width: 175 },
  { columnName: 'officeEmail', width: 250 }
];

const EMAIL_RECIPIENT_TYPES = {
  VENDOR: '38c8b61e-2a18-4e3f-9c59-3a5e9b1a1d2a',
  TRANSFEREE: 'c3b2c3c1-0324-4c7b-8b97-ef3f3a4a3f6e'
};

const EMAIL_API_TYPES = {
  MORREALE: 'b2966815-6493-4fe9-ada6-c40c0eb45de5'
};

export const LETTER_CATEGORIES = {
  EE: 'ee',
  VENDOR: 'vendor',
  MORREALE: 'morreale'
};

const isMorrealeLetter = template =>
  Boolean(
    template &&
    isIdEqual(template.apiRequestTypeId, EMAIL_API_TYPES.MORREALE)
  );

const matchesLetterCategory = (template, category) => {
  const morreale = isMorrealeLetter(template);

  if (category === LETTER_CATEGORIES.MORREALE) {
    return morreale;
  }

  if (morreale) {
    return false;
  }

  const recipientType =
    category === LETTER_CATEGORIES.VENDOR
      ? EMAIL_RECIPIENT_TYPES.VENDOR
      : EMAIL_RECIPIENT_TYPES.TRANSFEREE;

  return isIdEqual(template.templateUserTypeId, recipientType);
};


const AuthServiceEmailComponent = props => {
  const {
    id,
    communication,
    emailTemplates,
    defaultServiceId,
    authCommentServicesEnum,
    authCommentRegardingEnum,
    familyMembers = [],
    selectedTemplateProp: selectedTemplatePropId = null,
    openModalDefault = false,
    onUnselectTemplate,
    selectedAuthServiceId,
    vendorOptions = [],
    allVendorOptions = [],
    selectedSegmentServiceId,
    showFilterWidgets = false,
    selectedVendorId = null
  } = props;

  const defaultPrimaryEmail = communication?.primaryEmail ?? communication?.webAccessEmailAddress ?? '';

  const emailOptions = useMemo(() => {
    const familyEmails = Array.isArray(familyMembers)
      ? familyMembers.flatMap(member => [
        member?.personalEmailAddress,
        member?.workEmailAddress,
        member?.primaryEmailAddress,
        member?.secondaryEmailAddress,
        member?.emailAddress
      ])
      : [];

    return normalizeAndDeduplicateEmails(familyEmails);
  }, [familyMembers]);

  const defaultRecipientEmail = useMemo(() => {
    const primaryFamilyMember = Array.isArray(familyMembers) ? familyMembers.find(member => member?.isPrimary) : null;
    const primaryFamilyEmail = primaryFamilyMember?.primaryEmailAddress?.trim();

    if (primaryFamilyEmail && emailOptions.includes(primaryFamilyEmail)) {
      return primaryFamilyEmail;
    }

    if (emailOptions.length > 0) {
      return emailOptions[0];
    }

    return defaultPrimaryEmail;
  }, [familyMembers, emailOptions, defaultPrimaryEmail]);

  const [customEmailAddresses, setCustomEmailAddresses] = useState([]);
  const [customAddressInput, setCustomAddressInput] = useState('');
  // Vendor letters pick their vendor with the shared vendor search (as the Homesale and Destination pages do). The
  // service's own vendors (vendorOptions) are listed first, then every other vendor; the letter goes to the chosen
  // vendor's office email.
  const vendorSearchRows = useMemo(() => {
    const allVendors = Array.isArray(allVendorOptions) ? allVendorOptions : [];
    const serviceVendors = (Array.isArray(vendorOptions) ? vendorOptions : [])
      .map(v => allVendors.find(a => a.vendorId === v.vendorId) || v);
    const rows = [];
    [...serviceVendors, ...allVendors].forEach(v => {
      if (v && !rows.some(r => r.vendorId === v.vendorId)) {
        rows.push(v);
      }
    });
    return rows;
  }, [vendorOptions, allVendorOptions]);

  const [vendorId, setVendorId] = useState(selectedVendorId ?? null);
  const selectedVendor = vendorSearchRows.find(v => v.vendorId === vendorId) || null;
  const vendorEmails = (selectedVendor?.officeEmail || '').trim();

  const [open, setOpen] = useState(openModalDefault);

  const [step, setStep] = useState(STEP_SELECT);
  const [selectedTemplateId, setSelectedTemplateId] = useState(selectedTemplatePropId);
  const [selectedServiceId, setSelectedServiceId] = useState(defaultServiceId || ZERO_GUID);
  const [manualServiceId, setManualServiceId] = useState(defaultServiceId || ZERO_GUID);
  const [manualRegardingId, setManualRegardingId] = useState(ZERO_GUID);
  const [isClientViewable, setIsClientViewable] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isRendering, setIsRendering] = useState(false);

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  const [currentTemplateName, setCurrentTemplateName] = useState('');
  const [currentServiceId, setCurrentServiceId] = useState('');
  const [currentRegardingId, setCurrentRegardingId] = useState('');

  const [shouldDisableVendorSelection, setShouldDisableVendorSelection] = useState(false);

  const subjectInputRef = useRef(null);

  const templateOptions = useMemo(() => (Array.isArray(emailTemplates) ? emailTemplates : []), [emailTemplates]);
  const serviceOptions = useMemo(() => (Array.isArray(authCommentServicesEnum) ? authCommentServicesEnum.filter(s => s.enumType === 1).sort((a, b) =>
    a.title.localeCompare(b.title)
  ) : []), [authCommentServicesEnum]);
  const availableServiceIdSet = useMemo(() => new Set(serviceOptions.map(service => service.id?.toLowerCase()).filter(Boolean)), [serviceOptions]);

  const isTemplateServiceAvailable = template => {
    if (!template?.serviceId || isIdEqual(template.serviceId, ZERO_GUID)) {
      return true;
    }

    return availableServiceIdSet.has(template.serviceId.toLowerCase());
  };

  const filteredTemplateOptions = useMemo(
    () =>
      templateOptions.filter(
        t => isTemplateServiceAvailable(t) && (!selectedServiceId ? true : isIdEqual(t.serviceId, selectedServiceId))
      ),
    [templateOptions, selectedServiceId, availableServiceIdSet]
  );

  const selectedTemplate = useMemo(
    () =>
      filteredTemplateOptions.find(t => t.templateId === selectedTemplateId) ||
      templateOptions.find(t => t.templateId === selectedTemplateId),
    [filteredTemplateOptions, templateOptions, selectedTemplateId]
  );

  const [filteredRegardingOptions, setFilteredRegardingOptions] = useState(createFilteredRegardingOptions({ serviceId: manualServiceId }, authCommentRegardingEnum));



  useEffect(() => {
    if (!manualServiceId || manualServiceId === ZERO_GUID) {
      setManualRegardingId('');
      return;
    }

    const regardingStillExists = filteredRegardingOptions.some(option => isIdEqual(option.id, manualRegardingId));
    if (!regardingStillExists) {
      setManualRegardingId('');
    }
  }, [manualServiceId, manualRegardingId, filteredRegardingOptions]);

  const getEnumTitle = (list, id) => {
    if (!id || !Array.isArray(list)) return '';
    const match = list.find(x => isIdEqual(x.id, id));
    return match?.title || match?.description || '';
  };

  const currentServiceTitle = useMemo(() => getEnumTitle(authCommentServicesEnum, currentServiceId), [
    authCommentServicesEnum,
    currentServiceId
  ]);

  const currentRegardingTitle = useMemo(() => getEnumTitle(authCommentRegardingEnum, currentRegardingId), [
    authCommentRegardingEnum,
    currentRegardingId
  ]);


  const [useTemplate, setUseTemplate] = useState(true);
  const [letterCategory, setLetterCategory] = useState(LETTER_CATEGORIES.EE);

  const [isVendorTemplate, setIsVendorTemplate] = useState(
    selectedTemplate && isIdEqual(selectedTemplate.templateUserTypeId, EMAIL_RECIPIENT_TYPES.VENDOR) && !isMorrealeLetter(selectedTemplate)
  );

  const [isMorrealeTemplate, setIsMorrealeTemplate] = useState(
    selectedTemplate && isMorrealeLetter(selectedTemplate)
  );

  const visibleTemplateOptions = filteredTemplateOptions.filter(template =>
    showFilterWidgets ? matchesLetterCategory(template, letterCategory) : true
  );

  useEffect(() => {
    setIsVendorTemplate(
      Boolean(
        letterCategory === LETTER_CATEGORIES.VENDOR || (
          selectedTemplate &&
          isIdEqual(
            selectedTemplate.templateUserTypeId,
            EMAIL_RECIPIENT_TYPES.VENDOR
          ) && !isMorrealeLetter(selectedTemplate)
        )
      )
    );
  }, [useTemplate, selectedTemplate, isMorrealeTemplate, letterCategory]);

  useEffect(() => {
    setIsMorrealeTemplate(
      Boolean(
        letterCategory === LETTER_CATEGORIES.MORREALE || (
          selectedTemplate &&
          isMorrealeLetter(selectedTemplate)
        )
      )
    );
  }, [useTemplate, selectedTemplate, isMorrealeTemplate, letterCategory]);

  useEffect(() => {
    if (!isVendorTemplate) {
      setShouldDisableVendorSelection(false);
      return;
    }

    const isApiRequestTemplate = selectedTemplate && selectedTemplate.apiRequestTypeId === 'b2966815-6493-4fe9-ada6-c40c0eb45de5';
    setShouldDisableVendorSelection(Boolean(isApiRequestTemplate));

  }, [isVendorTemplate, selectedTemplate]);

  const [selectedEmails, setSelectedEmails] = useState(
    isVendorTemplate ? [] : openModalDefault && defaultRecipientEmail ? [defaultRecipientEmail] : []
  );

  const handleLetterCategoryChange = category => {
    setLetterCategory(category);
    setSelectedTemplateId('');
  };

  // const hasRecipients = isMorrealeTemplate
  //   ? true // The API delivery path determines the recipient.
  //   : isVendorTemplate
  //     ? vendorEmails.length > 0
  //     : selectedEmails.length > 0 || customEmailAddresses.length > 0;

  useEffect(() => {
    if (open) {
      setSelectedServiceId(defaultServiceId || '');
      setManualServiceId(defaultServiceId || '');
    }
  }, [open, defaultServiceId]);

  useEffect(() => {
    setSelectedServiceId(selectedTemplate?.serviceId || '');
  }, [selectedTemplate]);

  const handleOpen = () => {
    setOpen(true);
    setStep(STEP_SELECT);
    setSelectedTemplateId('');
    setSubject('');
    setBodyHtml('');
    setIsRendering(false);
    setCurrentTemplateName('');
    setCurrentServiceId('');
    setCurrentRegardingId('');
    setSelectedEmails(defaultRecipientEmail ? [defaultRecipientEmail] : []);
    setCustomEmailAddresses([]);
    setCustomAddressInput('');
    setVendorId(selectedVendorId ?? null);
    setManualServiceId(defaultServiceId || '');
    setManualRegardingId('');
    setIsClientViewable(false);
    setAttachments([]);
  };

  const handleClose = () => {
    setOpen(false);
    setStep(STEP_SELECT);
    setSelectedTemplateId('');
    setSubject('');
    setBodyHtml('');
    setIsRendering(false);
    setCurrentTemplateName('');
    setCurrentServiceId('');
    setCurrentRegardingId('');
    setSelectedEmails([]);
    setCustomEmailAddresses([]);
    setCustomAddressInput('');
    setManualServiceId(defaultServiceId || '');
    setManualRegardingId('');
    setIsClientViewable(false);
    setAttachments([]);
    if (onUnselectTemplate) {
      onUnselectTemplate();
    }
  };

  const handleServiceChange = e => {
    const newServiceId = e.target.value;
    setSelectedServiceId(newServiceId);
    setSelectedTemplateId('');
  };

  const [missingFieldsDialog, setMissingFieldsDialog] = useState({ open: false, fields: [], data: null });

  const applyRenderedEmail = data => {
    setSubject(data.renderedSubject || '');
    setBodyHtml(data.renderedHtmlBody || '');

    if (selectedTemplate) {
      setCurrentTemplateName(selectedTemplate.templateName || '');
      setCurrentServiceId(selectedTemplate.serviceId || '');
      setCurrentRegardingId(selectedTemplate.regardingId || '');
    } else {
      setCurrentTemplateName('');
      setCurrentServiceId('');
      setCurrentRegardingId('');
    }

    setStep(STEP_EDIT);
  };

  const handleRenderEmail = async () => {
    if (!useTemplate) {
      setSubject('');
      setBodyHtml('');
      setCurrentTemplateName('');
      setCurrentServiceId('');
      setCurrentRegardingId('');
      setStep(STEP_EDIT);
      return;
    }

    if (!selectedTemplateId) return;

    try {
      setIsRendering(true);

      const renderRequest = { segmentId: selectedSegmentServiceId };
      const resp = await props.postRenderAuthorizationEmailTemplate(selectedTemplateId, id, renderRequest);
      const data = resp?.response ?? resp;

      const missing = data?.validationResult?.nullOrEmptyTokens || [];
      if (missing.length > 0) {
        // Pause here; the dialog's OK button calls applyRenderedEmail(data)
        setMissingFieldsDialog({ open: true, fields: missing, data });
        return;
      }

      applyRenderedEmail(data);
    } catch (err) {
      console.error('Failed to render email template', err);
      props.showToast('Failed to render email template', 'error');
    } finally {
      setIsRendering(false);
    }
  };

  const closeMissingFieldsDialog = () => setMissingFieldsDialog({ open: false, fields: [], data: null });

  const confirmMissingFields = () => {
    const { data } = missingFieldsDialog;
    closeMissingFieldsDialog();
    if (data) applyRenderedEmail(data);
  };


  const convertToBase64 = file => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);

      fileReader.onload = () => {
        resolve(fileReader.result);
      };

      fileReader.onerror = error => {
        reject(error);
      };
    });
  };

  const addSelectedFiles = newFiles => {
    const merged = [...attachments, ...Array.from(newFiles || [])];
    const deduped = merged.filter((file, index, arr) => {
      const key = `${file.name}|${file.size}|${file.lastModified}`;
      return arr.findIndex(x => `${x.name}|${x.size}|${x.lastModified}` === key) === index;
    });
    setAttachments(deduped);
  };

  const removeAttachment = indexToRemove => {
    setAttachments(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const addCustomAddress = () => {
    const trimmed = (customAddressInput || '').trim();
    if (!trimmed) {
      return;
    }

    setCustomEmailAddresses(previous => normalizeAndDeduplicateEmails([...previous, trimmed]));
    setCustomAddressInput('');
  };

  const removeCustomAddress = indexToRemove => {
    setCustomEmailAddresses(previous => previous.filter((_, index) => index !== indexToRemove));
  };

  const handleSendClick = async () => {
    const emailAddresses = [];

    if (isVendorTemplate) {
      emailAddresses.push(vendorEmails);
    } else if (!isMorrealeTemplate) {
      normalizeAndDeduplicateEmails([...selectedEmails, ...customEmailAddresses]).forEach(email => emailAddresses.push(email));
    } else {
      emailAddresses.push('');
    }

    if (emailAddresses.length === 0) {
      props.showToast('No email address available to send to.', 'error');
      return;
    }

    const invalidEmails = emailAddresses.filter(email => !isLikelyEmail(email));
    if (!isMorrealeTemplate && invalidEmails.length > 0) {
      props.showToast('One or more recipient email addresses are invalid.', 'error');
      return;
    }

    const attachmentPayload = await Promise.all(
      attachments.map(async file => {
        let base64FileData = await convertToBase64(file);
        base64FileData = String(base64FileData).split(',')[1] || '';

        return {
          fileName: file.name,
          contentType: file.type,
          base64FileData
        };
      })
    );

    const resolvedServiceId = useTemplate
      ? selectedTemplate?.serviceId || currentServiceId || selectedServiceId || ZERO_GUID
      : manualServiceId || ZERO_GUID;

    const resolvedRegardingId = useTemplate ? selectedTemplate?.regardingId || currentRegardingId || ZERO_GUID : manualRegardingId || ZERO_GUID;

    const request = {
      authorizationId: parseInt(id, 10),
      renderedSubject: subject || '',
      renderedHtmlBody: bodyHtml || '',
      templateId: useTemplate && selectedTemplateId ? Number(selectedTemplateId) : 0,
      emailAddresses: emailAddresses,
      fromAddress: 'test-relo@trcgm.com',
      authServiceId: selectedAuthServiceId,
      segmentId: selectedSegmentServiceId,
      serviceId: resolvedServiceId,
      regardingId: resolvedRegardingId,
      isClientViewable,
      attachments: attachmentPayload,
      isMorrealeTemplate: isMorrealeTemplate
    };
    const sendTemplateId = useTemplate && selectedTemplateId ? selectedTemplateId : 0;

    setIsRendering(true);

    try {
      const response = await props.postSendRenderAuthorizationEmailTemplate(sendTemplateId, id, request);
      if (response.type === POST_SEND_RENDER_AUTHORIZATION_EMAIL_TEMPLATE_FAILURE) {
        props.showToast('Error sending template', TOAST_MESSAGE_SEVERITY_ERROR);
      } else {
        await props.getAuthorizationSentTemplates(id);
        await props.getAuthorizationComments(id);
        await props.getEmailTemplateSummaries();
        props.showToast('Email sent successfully.', 'success');
        handleClose();
      }
    } catch (err) {
      console.error('Failed to send rendered email', err);
      props.showToast('Failed to send email.', 'error');
    } finally {
      setIsRendering(false);
      if (onUnselectTemplate) {
        onUnselectTemplate();
      }
    }
  };

  return (
    <>
      {!openModalDefault && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '5px' }}>
            <Button
              variant="contained"
              onClick={handleOpen}
              sx={{
                maxHeight: '40px',
                marginLeft: '12px',
                marginTop: '8px',
                backgroundColor: '#868686',
                '&:hover': { backgroundColor: '#474747' }
              }}
            >
              Send Email
            </Button>
          </Box>
        </Box>
      )}
      <Modal open={open} onClose={handleClose} aria-labelledby="email-modal">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 1100,
            maxHeight: '100vh',
            overflowY: 'hidden',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {step === STEP_SELECT && (
            <>
              <Typography variant="h6" id="email-modal" gutterBottom>
                Select Email Recipients & Template
              </Typography>
              {isMorrealeTemplate && (
                <Box sx={{ mt: 1 }}>
                  <Hint>
                    This Morreale letter will be sent via API, not email. Recipient
                    selection is not available. Special formatting may not be rendered
                    properly in the preview.
                  </Hint>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                <Box sx={{ flex: 1 }}>
                  {!isMorrealeTemplate && !isVendorTemplate && (
                    <>
                      <FormControl fullWidth margin="dense">
                        <InputLabel id="recipient-email-select-label">Email Recipients</InputLabel>
                        <Select
                          labelId="recipient-email-select-label"
                          multiple
                          value={selectedEmails}
                          label="Email Recipients"
                          onChange={event => {
                            const value = event.target.value;
                            setSelectedEmails(normalizeAndDeduplicateEmails(Array.isArray(value) ? value : [value]));
                          }}
                          renderValue={selected => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map(email => (
                                <Chip key={email} label={email} size="small" />
                              ))}
                            </Box>
                          )}
                        >
                          {emailOptions.length === 0 && (
                            <MenuItem disabled value="">
                              No family member emails available
                            </MenuItem>
                          )}
                          {emailOptions.map(email => (
                            <MenuItem key={email} value={email}>
                              {email}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Custom Addresses
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Type an email and press Tab"
                          value={customAddressInput}
                          onChange={event => setCustomAddressInput(event.target.value)}
                          onBlur={addCustomAddress}
                          onKeyDown={event => {
                            if ((event.key === 'Tab' || event.key === 'Enter') && customAddressInput.trim()) {
                              event.preventDefault();
                              addCustomAddress();
                            }
                          }}
                        />
                        {customEmailAddresses.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {customEmailAddresses.map((email, index) => (
                              <Chip key={`${email}-${index}`} label={email} size="small" onDelete={() => removeCustomAddress(index)} />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                  {isVendorTemplate && (
                    <Box sx={{ mt: 1 }}>
                      <AdvancedSearchComponent
                        labelText="Vendor"
                        displayValueAccessorKey="companyName"
                        saveIdValueAccessorKey="vendorId"
                        searchingCriteria={VENDOR_SEARCH_COLUMNS}
                        searchingCriteriaExtensions={VENDOR_SEARCH_COLUMN_EXTENSIONS}
                        searchingRows={vendorSearchRows}
                        id="send-letter-vendor"
                        value={selectedVendor?.companyName || ''}
                        // The search's clear button reports an empty array rather than null
                        setIdValue={newVendorId => setVendorId(Array.isArray(newVendorId) ? null : newVendorId)}
                        disabled={shouldDisableVendorSelection}
                        fullWidth
                        size="small"
                      />
                      <Typography
                        variant="caption"
                        display="block"
                        color={selectedVendor && !isLikelyEmail(vendorEmails) ? 'error' : 'text.secondary'}
                        sx={{ mt: 0.5 }}
                      >
                        {!selectedVendor
                          ? 'Select the vendor to send this letter to'
                          : isLikelyEmail(vendorEmails)
                            ? `Sends to ${vendorEmails}`
                            : 'This vendor has no valid office email. Add one on the vendor record or pick another vendor.'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2
                    }}
                  >
                    <FormControlLabel
                      control={<Switch checked={useTemplate} onChange={e => setUseTemplate(e.target.checked)} color="primary" />}
                      label="Use Email Template"
                      sx={{ mr: 2 }}
                    />
                    {useTemplate && (
                      <FormControl variant="standard" sx={{ minWidth: 220 }}>
                        <InputLabel id="email-template-service-label">Service</InputLabel>
                        <Select
                          labelId="email-template-service-label"
                          value={selectedServiceId}
                          onChange={handleServiceChange}
                          label="Service"
                        >
                          <MenuItem value="">
                            <em>All services</em>
                          </MenuItem>
                          {serviceOptions.map(s => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.title || s.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>

                  {showFilterWidgets && useTemplate && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={letterCategory === LETTER_CATEGORIES.EE}
                            onChange={(_, checked) => {
                              if (checked) {
                                handleLetterCategoryChange(LETTER_CATEGORIES.EE);
                              }
                            }}
                            color="primary"
                          />
                        }
                        label="EE letters"
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={letterCategory === LETTER_CATEGORIES.VENDOR}
                            onChange={(_, checked) => {
                              if (checked) {
                                handleLetterCategoryChange(LETTER_CATEGORIES.VENDOR);
                              }
                            }}
                            color="primary"
                          />
                        }
                        label="Vendor letters"
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={letterCategory === LETTER_CATEGORIES.MORREALE}
                            onChange={(_, checked) => {
                              if (checked) {
                                handleLetterCategoryChange(LETTER_CATEGORIES.MORREALE);
                              }
                            }}
                            color="primary"
                          />
                        }
                        label="Morreale letters"
                      />
                    </Box>
                  )}

                  {useTemplate && (
                    <FormControl variant="standard" fullWidth sx={{ mt: 0.5 }}>
                      <InputLabel id="email-template-label">Select Template</InputLabel>
                      <Select
                        labelId="email-template-label"
                        value={selectedTemplateId}
                        onChange={e => setSelectedTemplateId(e.target.value)}
                        label="Select Template"
                      >
                        <MenuItem value="">
                          <em>Select a template...</em>
                        </MenuItem>
                        {visibleTemplateOptions.map(template => (
                          <MenuItem key={template.templateId} value={template.templateId}>
                            {template.templateName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {!useTemplate && (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Template is disabled. You’ll start with a blank email body.
                      </Typography>
                      <FormControl variant="standard" fullWidth sx={{ mt: 0.5 }}>
                        <InputLabel id="manual-email-service-label">Service</InputLabel>
                        <Select
                          labelId="manual-email-service-label"
                          value={manualServiceId}
                          onChange={e => {
                            setManualServiceId(e.target.value);
                            setFilteredRegardingOptions(createFilteredRegardingOptions({ serviceId: e.target.value }, authCommentRegardingEnum));
                          }}
                          label="Service"
                        >
                          <MenuItem value="">
                            <em>Select service...</em>
                          </MenuItem>
                          {serviceOptions.map(service => (
                            <MenuItem key={service.id} value={service.id}>
                              {service.title || service.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl variant="standard" fullWidth sx={{ mt: 0.5 }}>
                        <InputLabel id="manual-email-regarding-label">Regarding</InputLabel>
                        <Select
                          labelId="manual-email-regarding-label"
                          value={manualRegardingId}
                          onChange={e => setManualRegardingId(e.target.value)}
                          label="Regarding"
                          disabled={!manualServiceId}
                        >
                          <MenuItem value="">
                            <em>Select regarding...</em>
                          </MenuItem>
                          {filteredRegardingOptions.map(option => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.title || option.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '10px'
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRenderEmail}
                  disabled={
                    (useTemplate && !selectedTemplateId) ||
                    isRendering ||
                    (!isMorrealeTemplate && isVendorTemplate && !isLikelyEmail(vendorEmails)) ||
                    (!isMorrealeTemplate && !isVendorTemplate && selectedEmails.length === 0 && customEmailAddresses.length === 0)
                  }
                >
                  {isRendering ? 'Loading...' : 'Create Email'}
                </Button>
              </Box>
            </>
          )}
          {step === STEP_EDIT && (
            <>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  pr: 1
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Review Email
                </Typography>

                <Box sx={{ mb: 1, p: 1.5, borderRadius: 1, bgcolor: 'grey.100' }}>
                  {useTemplate && currentTemplateName && (
                    <>
                      <Typography variant="subtitle2">
                        Template: <strong>{currentTemplateName}</strong>
                      </Typography>
                      {currentServiceTitle && (
                        <Typography variant="caption" display="block">
                          Service: {currentServiceTitle}
                        </Typography>
                      )}
                      {currentRegardingTitle && (
                        <Typography variant="caption" display="block">
                          Regarding: {currentRegardingTitle}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block">
                        To: {isMorrealeTemplate ? 'N/a' : isVendorTemplate ? vendorEmails : normalizeAndDeduplicateEmails([...selectedEmails, ...customEmailAddresses]).join(', ')}
                      </Typography>
                    </>
                  )}
                  {!useTemplate && (
                    <Typography variant="caption" display="block">
                      To: {isMorrealeTemplate ? 'N/a' : isVendorTemplate ? vendorEmails : normalizeAndDeduplicateEmails([...selectedEmails, ...customEmailAddresses]).join(', ')}
                    </Typography>
                  )}
                </Box>

                {!isMorrealeTemplate && !isVendorTemplate && (
                  <FormControl fullWidth margin="dense" sx={{ mb: 1 }}>
                    <InputLabel id="review-recipient-email-select-label">Email Recipients</InputLabel>
                    <Select
                      labelId="review-recipient-email-select-label"
                      multiple
                      value={selectedEmails}
                      label="Email Recipients"
                      onChange={event => {
                        const value = event.target.value;
                        setSelectedEmails(normalizeAndDeduplicateEmails(Array.isArray(value) ? value : [value]));
                      }}
                      renderValue={selected => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map(email => (
                            <Chip key={email} label={email} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {emailOptions.length === 0 && (
                        <MenuItem disabled value="">
                          No family member emails available
                        </MenuItem>
                      )}
                      {emailOptions.map(email => (
                        <MenuItem key={email} value={email}>
                          {email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {!isMorrealeTemplate && !isVendorTemplate && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Custom Addresses
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Type an email and press Tab"
                      value={customAddressInput}
                      onChange={event => setCustomAddressInput(event.target.value)}
                      onBlur={addCustomAddress}
                      onKeyDown={event => {
                        if ((event.key === 'Tab' || event.key === 'Enter') && customAddressInput.trim()) {
                          event.preventDefault();
                          addCustomAddress();
                        }
                      }}
                    />
                    {customEmailAddresses.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {customEmailAddresses.map((email, index) => (
                          <Chip key={`${email}-${index}`} label={email} size="small" onDelete={() => removeCustomAddress(index)} />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {!useTemplate && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <FormControl variant="standard" fullWidth>
                      <InputLabel id="manual-edit-service-label">Service</InputLabel>
                      <Select
                        labelId="manual-edit-service-label"
                        value={manualServiceId}
                        onChange={e => {
                          setManualServiceId(e.target.value);
                          setFilteredRegardingOptions(createFilteredRegardingOptions({ serviceId: e.target.value }, authCommentRegardingEnum));
                        }}
                        label="Service"
                      >
                        <MenuItem value="">
                          <em>Select service...</em>
                        </MenuItem>
                        {serviceOptions.map(service => (
                          <MenuItem key={service.id} value={service.id}>
                            {service.title || service.description}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                      <InputLabel id="manual-edit-regarding-label">Regarding</InputLabel>
                      <Select
                        labelId="manual-edit-regarding-label"
                        value={manualRegardingId}
                        onChange={e => setManualRegardingId(e.target.value)}
                        label="Regarding"
                        disabled={!manualServiceId}
                      >
                        <MenuItem value="">
                          <em>Select regarding...</em>
                        </MenuItem>
                        {filteredRegardingOptions.map(option => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.title || option.description}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                <FormControlLabel
                  control={<Switch checked={isClientViewable} onChange={e => setIsClientViewable(e.target.checked)} color="primary" />}
                  label="Client Viewable"
                  sx={{ mt: 1 }}
                />

                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Attachments
                  </Typography>
                  <Button variant="outlined" component="label" size="small">
                    Add Attachments
                    <input
                      hidden
                      type="file"
                      multiple
                      onChange={event => {
                        addSelectedFiles(event.target.files || []);
                        event.target.value = '';
                      }}
                    />
                  </Button>
                  {attachments.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {attachments.map((file, index) => (
                        <Chip key={`${file.name}-${file.size}-${file.lastModified}`} label={file.name} onDelete={() => removeAttachment(index)} />
                      ))}
                    </Box>
                  )}
                </Box>

                {selectedTemplate && selectedTemplate.isApiRequest && (
                  <Box>
                    <Hint>This is an API Request Template and special formatting may not be rendered properly.</Hint>
                  </Box>
                )}

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Subject
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  inputRef={subjectInputRef}
                />

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Body
                </Typography>
                <Box
                  sx={{
                    minHeight: 300,
                    pb: 1,
                    '& .ql-container': {
                      minHeight: 250
                    }
                  }}
                >
                  <EmailEditorTiny initialHtml={bodyHtml} onHtmlChange={setBodyHtml} />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '16px',
                  pt: 2,
                  borderTop: '1px solid #e0e0e0',
                  flexShrink: 0,
                  bgcolor: 'background.paper'
                }}
              >
                <Box>
                  <Button onClick={() => setStep(STEP_SELECT)}>Back</Button>
                </Box>
                <Box>
                  <Button onClick={handleClose} sx={{ marginRight: '8px' }}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendClick}
                    disabled={isRendering || (!isVendorTemplate && !isMorrealeTemplate && selectedEmails.length === 0 && customEmailAddresses.length === 0)}
                  >
                    {isRendering ? 'Sending...' : 'Send'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      <Dialog open={missingFieldsDialog.open} onClose={closeMissingFieldsDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Missing or incomplete fields</DialogTitle>

        <DialogContent dividers sx={{ maxHeight: 320 }}>
          <Typography variant="body2" gutterBottom>
            The following fields were missing or incomplete when rendering this letter:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {missingFieldsDialog.fields.map((field, i) => (
              <li key={`${field}-${i}`}>
                <Typography variant="body2">{field}</Typography>
              </li>
            ))}
          </ul>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Click OK to generate the letter without this data.
          </Typography>
          <Box>
            <Button onClick={closeMissingFieldsDialog} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={confirmMissingFields}>
              OK
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

const mapStateToProps = state => ({});

export default compose(
  connect(mapStateToProps, {
    showToast,
    postRenderAuthorizationEmailTemplate,
    postSendRenderAuthorizationEmailTemplate,
    getAuthorizationSentTemplates,
    getAuthorizationComments,
    getEmailTemplateSummaries
  })
)(AuthServiceEmailComponent);
